import { useDrag } from "@use-gesture/react";
import classNames from "classnames";
import { action, autorun, comparer, computed, runInAction, toJS } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef } from "react";
import { atom } from "../atom";
import { appSettingsMobx, uiStateMobx } from "../SharedIframeState";
import { PaneViewMode, Position, Rect } from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import { getThemeColor, isPointInsideRect } from "../Utils";
import {
  activePaneStates,
  canvasAtom,
  canvasViewport,
  canvasViewportShouldAnimateAtom,
  focusedPaneIdAtom,
  headerHeightComputed,
  selectedPaneIdsAtom,
  shouldShowCanvasWiresComputed,
} from "./CanvasState";
import {
  getInputPaneIds,
  getViewportSize,
  getZoomWithDirection,
  OVERLAY_PANE_CLASSNAME,
  OVERLAY_PANE_HEADER_BUTTON_CLASSNAME,
  OVERLAY_PANE_HEADER_CLASSNAME,
  zoomToLevel,
} from "./SandboxUtils";

const shouldShowMinimapAtom = atom(false);
export function toggleMinimap() {
  runInAction(() => {
    shouldShowMinimapAtom.value = !shouldShowMinimapAtom.value;
  });
}

const PREVIEW_CANVAS_WIDTH = 256;
const PREVIEW_CANVAS_HEIGHT = 180;
const PREVIEW_CANVAS_PADDING = 96;

const Minimap = observer(() => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawSettingsRef = useRef<{
    offsetX: number;
    offsetY: number;
    scale: number;
  }>({ offsetX: 0, offsetY: 0, scale: 0 });
  const panesInfoComputed = computed(
    () =>
      canvasAtom.value.panes
        .filter(
          (pane) =>
            activePaneStates.get(pane.id)?.viewMode.get() !==
            PaneViewMode.Hidden
        )
        .map((pane) => {
          const activePaneState = activePaneStates.get(pane.id);
          return {
            id: pane.id,
            rect:
              activePaneState?.rect.get() !== undefined
                ? toJS(activePaneState.rect.get())
                : pane.rect,
            z: activePaneState?.zIndex.get() ?? pane.z,
            inputPaneIds: getInputPaneIds(pane),
            selected:
              focusedPaneIdAtom.value === pane.id ||
              selectedPaneIdsAtom.value.includes(pane.id),
          };
        }),
    { equals: comparer.structural }
  );

  const currentViewportDrawRectRef = useRef<Rect | undefined>(undefined);
  const mousePositionRef = useRef<Position>([-1, -1]);
  const isMouseInCurrentViewportDrawRectRef = useRef(false);
  const isMouseOverCanvasRef = useRef(false);

  function draw() {
    const panesInfo = panesInfoComputed.get();
    // we want to access .theme here so draw reruns when theme changes
    const _theme = appSettingsMobx.theme;
    const canvasElement = canvasRef.current!;
    const viewportRect = [
      ...canvasViewport.offset,
      ...getViewportSize(canvasViewport.zoom, headerHeightComputed.get()),
    ];

    const minPaneLeft = Math.min(...panesInfo.map((p) => p.rect[0]));
    const maxPaneRight = Math.max(
      ...panesInfo.map((p) => p.rect[0] + p.rect[2])
    );
    const minPaneTop = Math.min(...panesInfo.map((p) => p.rect[1]));
    const maxPaneBottom = Math.max(
      ...panesInfo.map((p) => p.rect[1] + p.rect[3])
    );

    const renderLeft = Math.min(minPaneLeft, 0);
    const renderRight = Math.max(maxPaneRight, renderLeft + viewportRect[2]);
    const renderTop = Math.min(minPaneTop, 0);
    const renderBottom = Math.max(maxPaneBottom, renderTop + viewportRect[3]);
    let offsetX = PREVIEW_CANVAS_PADDING - renderLeft;
    let offsetY = PREVIEW_CANVAS_PADDING - renderTop;
    const xScale =
      PREVIEW_CANVAS_WIDTH /
      (renderRight - renderLeft + 2 * PREVIEW_CANVAS_PADDING);
    const yScale =
      PREVIEW_CANVAS_HEIGHT /
      (renderBottom - renderTop + 2 * PREVIEW_CANVAS_PADDING);
    let scale: number;
    // choose the smaller scale and center on cross-axis
    if (xScale < yScale) {
      scale = xScale;
      offsetY +=
        (PREVIEW_CANVAS_HEIGHT / xScale - (renderBottom - renderTop)) / 2 -
        PREVIEW_CANVAS_PADDING;
    } else {
      scale = yScale;
      offsetX +=
        (PREVIEW_CANVAS_WIDTH / yScale - (renderRight - renderLeft)) / 2 -
        PREVIEW_CANVAS_PADDING;
    }
    const ctx = canvasElement.getContext("2d")!;
    ctx.save();
    ctx.scale(2, 2);
    ctx.lineWidth = 1;
    ctx.fillStyle = `rgb(${getThemeColor("--color-neutral-0")})`;
    ctx.fillRect(0, 0, PREVIEW_CANVAS_WIDTH, PREVIEW_CANVAS_HEIGHT);
    function transformX(x: number) {
      return Math.round((x + offsetX) * scale);
    }
    function transformY(y: number) {
      return Math.round((y + offsetY) * scale);
    }
    drawSettingsRef.current = { offsetX, offsetY, scale };
    if (shouldShowCanvasWiresComputed.get()) {
      ctx.strokeStyle = `rgb(${getThemeColor("--color-neutral-200")})`;
      panesInfo.forEach((p) => {
        p.inputPaneIds.forEach((inputPaneId) => {
          const inputPane = panesInfo.find((p) => p.id === inputPaneId)!;
          ctx.beginPath();
          ctx.moveTo(
            transformX(inputPane.rect[0] + inputPane.rect[2]),
            transformY(inputPane.rect[1])
          );
          ctx.lineTo(transformX(p.rect[0]), transformY(p.rect[1]));
          ctx.stroke();
        });
      });
    }
    ctx.fillStyle = `rgb(${getThemeColor("--color-neutral-50")})`;
    const paneStrokeColor = `rgb(${getThemeColor("--color-neutral-300")})`;
    const selectedStrokeColor = `rgb(${getThemeColor("--color-neutral-500")})`;
    [...panesInfo]
      .sort((a, b) => a.z - b.z)
      .forEach((p) => {
        ctx.beginPath();
        ctx.rect(
          transformX(p.rect[0]),
          transformY(p.rect[1]),
          Math.round(p.rect[2] * scale),
          Math.round(p.rect[3] * scale)
        );
        ctx.fill();
        ctx.strokeStyle = p.selected ? selectedStrokeColor : paneStrokeColor;
        ctx.stroke();
      });
    {
      ctx.beginPath();
      const currentViewportDrawRect: Rect = [
        transformX(viewportRect[0]),
        transformY(viewportRect[1]),
        Math.round(viewportRect[2] * scale),
        Math.round(viewportRect[3] * scale),
      ];
      isMouseInCurrentViewportDrawRectRef.current = isPointInsideRect(
        mousePositionRef.current,
        currentViewportDrawRect
      );
      const accent500Color = getThemeColor("--color-accent-500");
      ctx.fillStyle =
        isMouseOverCanvasRef.current &&
        isMouseInCurrentViewportDrawRectRef.current
          ? `rgb(${accent500Color} / 0.08)`
          : `rgb(${accent500Color} / 0.05)`;
      currentViewportDrawRectRef.current = currentViewportDrawRect;
      ctx.rect(
        currentViewportDrawRect[0],
        currentViewportDrawRect[1],
        currentViewportDrawRect[2],
        currentViewportDrawRect[3]
      );
      ctx.fill();
      ctx.strokeStyle = `rgb(${accent500Color} / 0.75)`;
      ctx.stroke();
    }
    ctx.restore();
  }

  useEffect(
    () =>
      autorun(() => {
        draw();
      }),
    []
  );

  const bindDrag = useDrag(({ first, last, xy, memo = {} }) => {
    const canvasRect = canvasRef.current!.getBoundingClientRect();
    let isFirstJump = false;
    if (first) {
      const currentViewportDrawRect = currentViewportDrawRectRef.current!;
      if (
        isPointInsideRect(xy, [
          canvasRect.x + currentViewportDrawRect[0],
          canvasRect.y + currentViewportDrawRect[1],
          currentViewportDrawRect[2],
          currentViewportDrawRect[3],
        ])
      ) {
        const rectCenter = [
          canvasRect.x +
            currentViewportDrawRect[0] +
            currentViewportDrawRect[2] / 2,
          canvasRect.y +
            currentViewportDrawRect[1] +
            currentViewportDrawRect[3] / 2,
        ];
        memo.mouseOffset = [xy[0] - rectCenter[0], xy[1] - rectCenter[1]];
      } else {
        isFirstJump = true;
      }
    }

    const { mouseOffset } = memo;
    const offsetX = xy[0] - (mouseOffset?.[0] ?? 0) - canvasRect.left;
    const offsetY = xy[1] - (mouseOffset?.[1] ?? 0) - canvasRect.top;
    const drawSettings = drawSettingsRef.current;
    const centerPoint = [
      offsetX / drawSettings.scale - drawSettings.offsetX,
      offsetY / drawSettings.scale - drawSettings.offsetY,
    ];
    const [viewportWidth, viewportHeight] = getViewportSize(
      canvasViewport.zoom,
      headerHeightComputed.get()
    );
    const offset = [
      centerPoint[0] - viewportWidth / 2,
      centerPoint[1] - viewportHeight / 2,
    ];
    runInAction(() => {
      canvasViewportShouldAnimateAtom.value = isFirstJump;
      canvasViewport.offset = [Math.round(offset[0]), Math.round(offset[1])];
    });

    return memo;
  });

  return (
    <canvas
      {...bindDrag()}
      onMouseMove={(e) => {
        const canvasRect = canvasRef.current!.getBoundingClientRect();
        mousePositionRef.current = [
          e.clientX - canvasRect.x,
          e.clientY - canvasRect.y,
        ];
        if (
          isPointInsideRect(
            mousePositionRef.current,
            currentViewportDrawRectRef.current!
          ) !== isMouseInCurrentViewportDrawRectRef.current
        ) {
          draw();
        }
      }}
      onMouseEnter={() => {
        isMouseOverCanvasRef.current = true;
      }}
      onMouseLeave={() => {
        isMouseOverCanvasRef.current = false;
      }}
      width={`${PREVIEW_CANVAS_WIDTH * 2}px`}
      height={`${PREVIEW_CANVAS_HEIGHT * 2}px`}
      style={{
        width: `${PREVIEW_CANVAS_WIDTH}px`,
        height: `${PREVIEW_CANVAS_HEIGHT}px`,
      }}
      className="cursor-move touch-none"
      ref={canvasRef}
    ></canvas>
  );
});

const HeaderZoomControls = () => {
  return (
    <div className="flex items-center">
      <button
        onClick={() => {
          const nextZoom = getZoomWithDirection(canvasViewport.zoom, "out");
          if (nextZoom !== undefined) {
            zoomToLevel(nextZoom);
          }
        }}
        className="w-5 h-6 flex items-center justify-center hover:text-neutral-200 dark:text-neutral-400 dark:hover:text-neutral-600 transition"
      >
        <FeatherIcon icon="minus" size="sm" />
      </button>
      <button
        onClick={() => {
          const nextZoom = getZoomWithDirection(canvasViewport.zoom, "in");
          if (nextZoom !== undefined) {
            zoomToLevel(nextZoom);
          }
        }}
        className="w-5 h-6 flex items-center justify-center hover:text-neutral-200 dark:text-neutral-400 dark:hover:text-neutral-600 transition"
      >
        <FeatherIcon icon="plus" size="sm" />
      </button>
    </div>
  );
};

const MinimapPaneImpl = observer(() => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const offsetRef = useRef<Position>([0, 0]);

  const bindDrag = useDrag(
    ({ target, first, last, offset }) => {
      offsetRef.current = offset;
      rootRef.current!.style.transform = `translate(${offset[0]}px, ${offset[1]}px)`;
      if (first && target instanceof HTMLElement) {
        target.style.cursor = "grabbing";
      }
      if (last && target instanceof HTMLElement) {
        target.style.cursor = "";
      }
    },
    { from: () => offsetRef.current }
  );

  return (
    <div
      className={classNames(
        "fixed right-4 top-16 rounded overflow-hidden shadow-lg",
        OVERLAY_PANE_CLASSNAME,
        {
          hidden: computed(
            () => uiStateMobx.maximizedPaneId !== undefined
          ).get(),
        }
      )}
      ref={rootRef}
    >
      <div
        {...bindDrag()}
        className={classNames(
          OVERLAY_PANE_HEADER_CLASSNAME,
          "h-8 px-2 flex items-center cursor-grab touch-none"
        )}
      >
        <button
          onClick={action(() => {
            shouldShowMinimapAtom.value = false;
          })}
          className={classNames(OVERLAY_PANE_HEADER_BUTTON_CLASSNAME, "mr-2")}
        >
          <FeatherIcon icon="x" size="xs" />
        </button>
        <div className="font-medium">map</div>
        <div className="grow" />
        <HeaderZoomControls />
      </div>
      <Minimap />
    </div>
  );
});

export const MinimapPane = observer(() => {
  if (shouldShowMinimapAtom.value) {
    return <MinimapPaneImpl />;
  }
  return null;
});
